import { Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { usePinnedTips } from "@/hooks/usePinnedTips";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";

interface PinButtonProps {
  tipId: string;
  size?: "sm" | "default" | "lg";
  variant?: "default" | "outline" | "ghost";
  className?: string;
}

export const PinButton: React.FC<PinButtonProps> = ({ 
  tipId, 
  size = "default", 
  variant = "outline",
  className 
}) => {
  const { user } = useAuth();
  const { isPinned, pinTip, unpinTip } = usePinnedTips();
  const pinned = isPinned(tipId);

  const handleClick = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent event bubbling
    
    if (!user) {
      // Could redirect to auth page or show login modal
      return;
    }

    if (pinned) {
      await unpinTip(tipId);
    } else {
      await pinTip(tipId);
    }
  };

  if (!user) {
    return null; // Don't show pin button for non-authenticated users
  }

  return (
    <Button
      variant={variant}
      size={size}
      onClick={handleClick}
      className={cn(
        "transition-all duration-200",
        pinned && "text-red-500 hover:text-red-600",
        className
      )}
      title={pinned ? "Remove from favorites" : "Add to favorites"}
    >
      <Heart 
        className={cn(
          "h-4 w-4",
          size === "sm" && "h-3 w-3",
          size === "lg" && "h-5 w-5",
          pinned && "fill-current"
        )} 
      />
      {size === "lg" && (
        <span className="ml-2">
          {pinned ? "Unpin" : "Pin"}
        </span>
      )}
    </Button>
  );
};